/**
 * Notifications service — in-app notification bell for Slack thread activity.
 *
 * Reads from the `notifications` table (populated by `api/slack-events.js` whenever
 * someone replies to an AlderCortex-started thread) through a Clerk-authed,
 * service-role endpoint (`/api/notifications`). The table is deny-all to the anon
 * role, so we never query it directly from the browser.
 *
 * Realtime isn't available without anon SELECT on the table, so the bell refreshes
 * by polling (see `subscribeToNotifications`).
 *
 * Read state is persisted per-user in Supabase (notification_reads table) via the
 * /api/notifications POST endpoint, so it syncs across all devices for the same user.
 */

// Legacy localStorage key — kept only for migration; new reads go server-side.
const READ_KEY = 'aj:notifications:read'

/**
 * Get a Clerk token, waiting briefly for the getter to exist. NotificationBell
 * mounts as a child of OutlineView, so its first fetch can fire before the parent
 * effect installs window.__clerkGetToken — without this wait that first request
 * goes out unauthenticated and 401s (leaving the bell empty until a reload).
 */
async function getClerkToken() {
  for (let i = 0; i < 25; i++) {
    if (typeof window.__clerkGetToken === 'function') {
      try { const t = await window.__clerkGetToken(); if (t) return t } catch { /* retry */ }
    }
    await new Promise(r => setTimeout(r, 150))
  }
  return null
}

/**
 * Fetch notifications AND the user's server-side read state in one call.
 * Returns { notifications, readIds: Set<string> }.
 * Falls back to the legacy localStorage set so existing local reads still count.
 */
export async function getNotifications() {
  const token = await getClerkToken()
  const res = await fetch('/api/notifications', {
    headers: token ? { Authorization: 'Bearer ' + token } : {},
  })
  if (!res.ok) throw new Error('Notifications request failed: ' + res.status)
  const data = await res.json()
  // Merge server read IDs with any legacy localStorage IDs
  const serverIds = new Set(data.readIds || [])
  const localIds  = getLocalReadIds()
  localIds.forEach(id => serverIds.add(id))
  return { notifications: data.notifications || [], readIds: serverIds }
}

/** Local-only read IDs (legacy fallback — new reads go server-side). */
function getLocalReadIds() {
  try { return new Set(JSON.parse(localStorage.getItem(READ_KEY) || '[]')) }
  catch { return new Set() }
}

/**
 * Mark notifications as read for this user. Persists BOTH locally (so read state always sticks in
 * this browser, even if the server write fails) and server-side (so it syncs across devices when the
 * write succeeds). getNotifications merges the local set back in on every poll, so a failed (or
 * "ok but not persisted") server write can never resurrect a read item.
 */
export async function markRead(ids) {
  if (!ids || !ids.length) return
  // 1. Local first — guaranteed durable for this browser regardless of the server result.
  try {
    const current = getLocalReadIds()
    ids.forEach(id => current.add(id))
    localStorage.setItem(READ_KEY, JSON.stringify([...current]))
  } catch (_) { /* localStorage unavailable — server write below is the only path */ }
  // 2. Server (best-effort) — cross-device sync. Failures are already covered by the local write.
  const token = await getClerkToken()
  try {
    const res = await fetch('/api/notifications', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: 'Bearer ' + token } : {}),
      },
      body: JSON.stringify({ notificationIds: ids }),
    })
    if (!res.ok) console.warn('[notifications] markRead server write failed (' + res.status + ') — kept locally')
  } catch (e) {
    console.warn('[notifications] markRead request failed — kept locally', e)
  }
}

/** @deprecated — kept for legacy callers that expected a sync Set return */
export function getReadIds() {
  return getLocalReadIds()
}
